import CustomSelect from "components/custom-select/CustomSelect";
import { produce } from "immer";
import {
  ElementViewForClipboardProps,
  ElementViewProps,
} from "../AtomicElementView";
import styles from "./ShortAnswerGroupContent.module.scss";
import getClasses from "utils/getClasses";

interface ShortAnswerGroupData {
  [itemId: string]: string;
}

function parseRawData(data): ShortAnswerGroupData {
  if (!data || Array.isArray(data) || typeof data !== "object") {
    return {};
  }

  return data;
}

export function ShortAnswerGroupContentForClipboard({
  element,
  data: rawData,
}: ElementViewForClipboardProps<ShortAnswerGroupElement>) {
  const data = parseRawData(rawData);

  return (
    <div>
      {element.items.map((item) => (
        <div key={item.id}>
          <div>{item.title}</div>
          <ul>
            <li className={styles.itemContentInPrintView}>
              {data[item.id] || "No answer"}
            </li>
          </ul>
        </div>
      ))}
    </div>
  );
}

export default function ShortAnswerGroupContent({
  data: rawData,
  element,
  onChange,
  isLocked = false,
  isPrintMode = false,
  disabled = false,
  showAsMissing = false,
}: ElementViewProps<ShortAnswerGroupElement>) {
  const data = parseRawData(rawData);

  if (isPrintMode) {
    return (
      <div className={styles.container}>
        {element.items.map((item) => (
          <div className={styles.item} key={item.id}>
            <div className={styles.itemTitle}>{item.title}</div>
            <ul>
              <li className={styles.itemContentInPrintView}>
                {data[item.id] || "No answer"}
              </li>
            </ul>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {element.items.map((item) => (
        <div className={styles.item} key={item.id}>
          <div className={styles.itemTitle}>{item.title}</div>
          <ShortAnswerGroupItemContent
            item={item}
            data={data[item.id]}
            onChange={(value) =>
              onChange(
                produce(data, (draft) => {
                  draft[item.id] = value;
                }),
              )
            }
            isLocked={isLocked}
            disabled={disabled}
            showAsMissing={showAsMissing}
          />
        </div>
      ))}
    </div>
  );
}

export function ShortAnswerGroupItemContent({
  item,
  data,
  onChange,
  isLocked,
  disabled,
  showAsMissing,
}: {
  item: ShortAnswerGroupItem;
  data?: string;
  onChange: (value: string) => void;
  isLocked: boolean;
  disabled: boolean;
  showAsMissing?: boolean;
}) {
  switch (item.type) {
    case "short-answer-item":
      return (
        <input
          className={getClasses(
            styles.shortAnswerInput,
            showAsMissing && !data && styles.missing,
          )}
          value={data}
          placeholder="Enter Short Answer"
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled || isLocked}
        />
      );
    case "autocomplete-item":
      const selectOptions = item.options
        .filter((option) => option.title)
        .map((option) => ({
          value: option.title,
          label: option.title,
        }));
      return (
        <div>
          <CustomSelect
            value={selectOptions.find((el) => el.value === data)}
            placeholder="Choose Answer"
            options={selectOptions}
            onChange={({ value }) => onChange(value)}
            useNotesAndFormsV2Styles
            menuPortalTarget={document.body}
            disabled={disabled || isLocked}
            showAsInvalid={showAsMissing && !data}
          />
        </div>
      );
  }
}
